import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { syncSearchParams } from '../lib/urlSync';
import { getCached, swrFetch, invalidatePrefix } from '../lib/dataCache';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { tasksAPI, usersAPI, prospectsAPI, teamsV2API } from '../lib/api';
import { INTERNAL_THEMES } from '../lib/internalThemes';
import {
    Plus, Search, Clock, AlertTriangle, CheckCircle,
    Circle, Calendar, User, Trash2, Edit, Loader2, ListTodo,
    ArrowUp, ArrowRight, ArrowDown, Zap, Bell, Tag
} from 'lucide-react';
import { cn } from '../lib/utils';

function getPriorityConfig(priority) {
    if (priority === 'low') return { label: 'Basse', color: 'bg-slate-100 text-slate-600', Icon: ArrowDown };
    if (priority === 'medium') return { label: 'Moyenne', color: 'bg-blue-100 text-blue-700', Icon: ArrowRight };
    if (priority === 'high') return { label: 'Haute', color: 'bg-orange-100 text-orange-700', Icon: ArrowUp };
    if (priority === 'urgent') return { label: 'Urgente', color: 'bg-red-100 text-red-700', Icon: Zap };
    return { label: priority, color: 'bg-slate-100 text-slate-600', Icon: ArrowRight };
}

function formatDate(dateStr) {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' });
}

function isOverdue(dateStr, status) {
    if (!dateStr || status === 'done' || status === 'postponed') return false;
    return new Date(dateStr) < new Date();
}

// Plage de dates pour le filtre période
function periodRange(period) {
    const now = new Date();
    const start = new Date(now); start.setHours(0, 0, 0, 0);
    if (period === 'today') return [start, new Date(start.getTime() + 86400000)];
    if (period === 'week') {
        const dow = start.getDay() || 7; // lundi = 1
        const ws = new Date(start); ws.setDate(start.getDate() - (dow - 1));
        const we = new Date(ws); we.setDate(ws.getDate() + 7);
        return [ws, we];
    }
    if (period === 'month') {
        return [new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 1)];
    }
    return null;
}

function taskUsers(task) {
    const ids = new Set([...(task.assignee_ids || [])]);
    if (task.assigned_to) ids.add(task.assigned_to);
    if (task.created_by) ids.add(task.created_by);
    return ids;
}

export default function Tasks() {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [tasks, setTasks] = useState([]);
    const [users, setUsers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [prospects, setProspects] = useState([]);
    const [prospectsLoaded, setProspectsLoaded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [saving, setSaving] = useState(false);
    const [teamMembers, setTeamMembers] = useState([]);
    // Prolongation de tâche (raison + nouvelle date plus lointaine)
    const [postponeTask, setPostponeTask] = useState(null);
    const [postponeReason, setPostponeReason] = useState('');
    const [postponeDate, setPostponeDate] = useState('');

    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;
    const isAdminFull = user?.role === 'admin_full' || user?.is_admin === true;
    const isVP = user?.role === 'vp';
    const isApporteur = user?.role === 'apporteur';

    // Tri par échéance + sélection multiple (Full Admin)
    const [sortDir, setSortDir] = useState('asc'); // asc = échéance la plus proche
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Filtres (préservés dans l'URL)
    const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all'); // all|todo|in_progress|done|overdue
    const [filterPriority, setFilterPriority] = useState(searchParams.get('priority') || 'all');
    const [filterPeriod, setFilterPeriod] = useState(searchParams.get('period') || 'all'); // all|today|week|month
    const [filterTeam, setFilterTeam] = useState(searchParams.get('team') || ''); // admin only
    const [filterUser, setFilterUser] = useState(searchParams.get('member') || ''); // admin & vp
    const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');

    useEffect(() => {
        const params = new URLSearchParams();
        if (filterStatus !== 'all') params.set('status', filterStatus);
        if (filterPriority !== 'all') params.set('priority', filterPriority);
        if (filterPeriod !== 'all') params.set('period', filterPeriod);
        if (filterTeam) params.set('team', filterTeam);
        if (filterUser) params.set('member', filterUser);
        if (searchTerm) params.set('q', searchTerm);
        syncSearchParams(setSearchParams, searchParams, params);
    }, [filterStatus, filterPriority, filterPeriod, filterTeam, filterUser, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

    const [form, setForm] = useState({
        title: '', description: '', priority: 'medium', due_date: '',
        assigned_to: '', assignee_ids: [], prospect_id: '', module: 'easy_power', category: 'dossier', theme: '', is_team_task: false
    });

    // Utilisateurs assignables / filtrables selon le rôle
    const assignableUsers = useMemo(() => {
        if (isAdmin) return users.filter(u => u.is_active !== false);
        if (isVP) {
            const list = [user];
            users.forEach(u => {
                if (u.id === user.id || u.is_active === false) return;
                if (u.role === 'admin_full' || u.role === 'admin') { list.push(u); return; }
                if (u.vp_id === user.id) { list.push(u); return; }
            });
            teamMembers.forEach(m => { if (!list.find(a => a.id === m.id)) list.push(m); });
            return list;
        }
        if (isApporteur) {
            const list = [user];
            users.forEach(u => {
                if (u.id === user.id || u.is_active === false) return;
                if (u.role === 'admin_full' || u.role === 'admin') { list.push(u); return; }
                if (u.id === user.vp_id) { list.push(u); return; }
                if (u.role === 'apporteur' && u.vp_id === user.vp_id && user.vp_id) { list.push(u); return; }
            });
            return list;
        }
        return [];
    }, [users, user, isAdmin, isVP, isApporteur, teamMembers]);

    const loadData = useCallback(async () => {
        const params = {};
        if (isVP) params.team_scope = 'true'; // self + équipe
        // admin: {} = toutes ; apporteur: {} = les siennes (scopé backend)

        const key = `tasks:${user?.id}:${user?.role}`;
        const cached = getCached(key);
        if (cached) {
            setTasks(cached.tasks); setUsers(cached.users); setTeams(cached.teams || []);
            if (cached.teamMembers) setTeamMembers(cached.teamMembers);
            setLoading(false);
        } else { setLoading(true); }
        try {
            const data = await swrFetch(key, async () => {
                const [tasksRes, usersRes, teamsRes, vpExtra] = await Promise.all([
                    tasksAPI.getAll(params),
                    usersAPI.getAll().catch(() => ({ data: [user] })),
                    isAdmin ? teamsV2API.getAll().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
                    isVP ? usersAPI.getAssignableUsers(user.id).catch(() => ({ data: [] })) : Promise.resolve(null),
                ]);
                return {
                    tasks: tasksRes.data || [],
                    users: usersRes.data || [user],
                    teams: teamsRes.data || [],
                    teamMembers: vpExtra ? (vpExtra.data || []) : undefined,
                };
            });
            setTasks(data.tasks); setUsers(data.users); setTeams(data.teams || []);
            if (data.teamMembers) setTeamMembers(data.teamMembers);
        } catch (err) {
            if (!cached) toast.error('Erreur lors du chargement');
        } finally { setLoading(false); }
    }, [isAdmin, isVP, user]);

    useEffect(() => { loadData(); }, [loadData]);

    // Charger les prospects uniquement à l'ouverture du formulaire (allège la page)
    const ensureProspects = useCallback(async () => {
        if (prospectsLoaded) return;
        try {
            const res = await prospectsAPI.getAll({ page_size: 500 });
            setProspects(res.data || []);
            setProspectsLoaded(true);
        } catch { /* ignore */ }
    }, [prospectsLoaded]);

    // Membres d'une équipe (admin) pour le filtre équipe
    const teamMemberIds = useMemo(() => {
        if (!filterTeam) return null;
        const t = teams.find(x => x.id === filterTeam);
        return t ? new Set(t.member_ids || []) : new Set();
    }, [filterTeam, teams]);

    // baseFiltered = tout SAUF le statut (les cartes comptent là-dessus)
    const baseFiltered = useMemo(() => {
        const range = periodRange(filterPeriod);
        return tasks.filter(task => {
            if (searchTerm) {
                const q = searchTerm.toLowerCase();
                if (!(task.title?.toLowerCase().includes(q) || task.description?.toLowerCase().includes(q) || task.prospect_name?.toLowerCase().includes(q))) return false;
            }
            if (filterPriority !== 'all' && task.priority !== filterPriority) return false;
            if (range) {
                const ref = new Date(task.due_date || task.created_at);
                if (!(ref >= range[0] && ref < range[1])) return false;
            }
            if (filterTeam && teamMemberIds) {
                const tu = taskUsers(task);
                if (![...teamMemberIds].some(id => tu.has(id))) return false;
            }
            if (filterUser) {
                if (!taskUsers(task).has(filterUser)) return false;
            }
            return true;
        });
    }, [tasks, searchTerm, filterPriority, filterPeriod, filterTeam, teamMemberIds, filterUser]);

    const counts = useMemo(() => ({
        total: baseFiltered.length,
        todo: baseFiltered.filter(t => t.status === 'todo').length,
        postponed: baseFiltered.filter(t => t.status === 'postponed').length,
        done: baseFiltered.filter(t => t.status === 'done').length,
        overdue: baseFiltered.filter(t => isOverdue(t.due_date, t.status)).length,
    }), [baseFiltered]);

    const displayedTasks = useMemo(() => {
        let list;
        if (filterStatus === 'all') list = baseFiltered;
        else if (filterStatus === 'overdue') list = baseFiltered.filter(t => isOverdue(t.due_date, t.status));
        else list = baseFiltered.filter(t => t.status === filterStatus);
        // Tri par échéance (les tâches sans échéance vont à la fin)
        return [...list].sort((a, b) => {
            const av = a.due_date || '', bv = b.due_date || '';
            if (!av && !bv) return 0;
            if (!av) return 1;
            if (!bv) return -1;
            return sortDir === 'asc' ? (av < bv ? -1 : 1) : (av < bv ? 1 : -1);
        });
    }, [baseFiltered, filterStatus, sortDir]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) { toast.error('Le titre est obligatoire'); return; }
        if (form.category === 'dossier') {
            if (!form.prospect_id) { toast.error('Une tâche « dossier » doit être liée à un prospect'); return; }
            if (!form.module) { toast.error('Choisissez le module (Easy Power ou Chantier)'); return; }
        } else if (!form.theme) {
            toast.error('Choisissez un thème pour la tâche interne');
            return;
        }
        setSaving(true);
        try {
            const payload = { ...form };
            if (!Array.isArray(payload.assignee_ids) || payload.assignee_ids.length === 0) {
                payload.assignee_ids = payload.assigned_to ? [payload.assigned_to] : [];
            }
            payload.assigned_to = payload.assignee_ids[0] || '';
            if (editingTask) { await tasksAPI.update(editingTask.id, payload); toast.success('Tâche mise à jour'); }
            else { await tasksAPI.create(payload); toast.success('Tâche créée'); }
            setDialogOpen(false); resetForm();
            invalidatePrefix('tasks'); invalidatePrefix('calendar'); loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally { setSaving(false); }
    };

    const handleStatusChange = async (taskId, newStatus) => {
        if (newStatus === 'postponed') {
            // Ouvrir le dialog de prolongation (raison + nouvelle date plus lointaine obligatoires)
            const task = tasks.find(t => t.id === taskId);
            setPostponeTask(task || { id: taskId });
            setPostponeReason('');
            setPostponeDate('');
            return;
        }
        try {
            await tasksAPI.update(taskId, { status: newStatus });
            invalidatePrefix('tasks'); invalidatePrefix('calendar'); loadData();
        } catch { toast.error('Erreur'); }
    };

    const submitPostpone = async () => {
        if (!postponeReason.trim()) { toast.error('Indiquez une raison de prolongation'); return; }
        if (!postponeDate) { toast.error('Choisissez une nouvelle date (plus lointaine)'); return; }
        try {
            setSaving(true);
            await tasksAPI.update(postponeTask.id, {
                status: 'postponed',
                postpone_reason: postponeReason.trim(),
                due_date: postponeDate,
            });
            toast.success('Tâche prolongée');
            setPostponeTask(null);
            invalidatePrefix('tasks'); invalidatePrefix('calendar'); loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la prolongation');
        } finally { setSaving(false); }
    };

    const handleRemind = async (taskId) => {
        try {
            const res = await tasksAPI.remind(taskId);
            toast.success(`Relance envoyée à ${res.data?.count ?? ''} personne(s)`);
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur lors de la relance'); }
    };

    const handleDelete = async (taskId) => {
        if (!window.confirm('Supprimer cette tâche ?')) return;
        try {
            await tasksAPI.delete(taskId); toast.success('Tâche supprimée');
            invalidatePrefix('tasks'); invalidatePrefix('calendar'); loadData();
        } catch { toast.error('Erreur'); }
    };

    const toggleSelect = (id) => setSelectedIds(prev => {
        const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
    });
    const allDisplayedSelected = displayedTasks.length > 0 && displayedTasks.every(t => selectedIds.has(t.id));
    const toggleSelectAll = () => setSelectedIds(() => allDisplayedSelected ? new Set() : new Set(displayedTasks.map(t => t.id)));
    const handleBulkDelete = async () => {
        const ids = [...selectedIds];
        if (ids.length === 0) return;
        if (!window.confirm(`Supprimer définitivement ${ids.length} tâche(s) ?`)) return;
        try {
            const res = await tasksAPI.bulkDelete(ids);
            toast.success(`${res.data?.deleted ?? ids.length} tâche(s) supprimée(s)`);
            setSelectedIds(new Set());
            invalidatePrefix('tasks'); invalidatePrefix('calendar'); loadData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    };

    const openEdit = (task) => {
        ensureProspects();
        setEditingTask(task);
        setForm({
            title: task.title, description: task.description || '', priority: task.priority,
            due_date: task.due_date || '', assigned_to: task.assigned_to || '',
            assignee_ids: task.assignee_ids || (task.assigned_to ? [task.assigned_to] : []),
            prospect_id: task.prospect_id || '', module: task.module || 'easy_power',
            category: task.category || 'dossier', theme: task.theme || '',
            is_team_task: task.is_team_task || false
        });
        setDialogOpen(true);
    };

    const resetForm = () => {
        setEditingTask(null);
        setForm({ title: '', description: '', priority: 'medium', due_date: '', assigned_to: '', assignee_ids: [], prospect_id: '', module: 'easy_power', category: 'dossier', theme: '', is_team_task: false });
    };

    const STATUS_CARDS = [
        { key: 'all', label: 'Total', value: counts.total, Icon: ListTodo, active: 'ring-slate-400', bg: 'bg-slate-50', text: 'text-slate-700' },
        { key: 'todo', label: 'À faire', value: counts.todo, Icon: Circle, active: 'ring-slate-400', bg: 'bg-slate-50', text: 'text-slate-700' },
        { key: 'postponed', label: 'Prolongées', value: counts.postponed, Icon: Clock, active: 'ring-amber-400', bg: 'bg-amber-50', text: 'text-amber-700' },
        { key: 'done', label: 'Terminées', value: counts.done, Icon: CheckCircle, active: 'ring-green-400', bg: 'bg-green-50', text: 'text-green-700' },
        { key: 'overdue', label: 'En retard', value: counts.overdue, Icon: AlertTriangle, active: 'ring-red-400', bg: counts.overdue > 0 ? 'bg-red-50' : 'bg-slate-50', text: counts.overdue > 0 ? 'text-red-700' : 'text-slate-700' },
    ];

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="tasks-page">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-outfit font-semibold text-slate-900">
                            {isAdmin ? 'Pilotage des tâches' : isVP ? 'Tâches de mon équipe' : 'Mes tâches'}
                        </h1>
                        <p className="text-slate-500 mt-1">Chaque tâche est liée à un dossier · suivez qui fait quoi</p>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (open) ensureProspects(); else resetForm(); }}>
                        <DialogTrigger asChild>
                            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="new-task-btn">
                                <Plus className="w-4 h-4 mr-2" />Nouvelle tâche
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle>{editingTask ? 'Modifier la tâche' : 'Nouvelle tâche'}</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Titre *</Label>
                                    <Input value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Ex: Appeler le prospect Dupont" required data-testid="task-title-input" />
                                </div>
                                {/* Nature : Dossier (commercial) ou Interne (organisationnel) */}
                                <div className="space-y-2">
                                    <Label>Nature</Label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button type="button" onClick={() => setForm(p => ({ ...p, category: 'dossier' }))}
                                            data-testid="task-category-dossier"
                                            className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${form.category === 'dossier' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                                            🏢 Dossier
                                        </button>
                                        <button type="button" onClick={() => setForm(p => ({ ...p, category: 'interne' }))}
                                            data-testid="task-category-interne"
                                            className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${form.category === 'interne' ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                                            🗂️ Interne
                                        </button>
                                    </div>
                                </div>
                                {form.category === 'dossier' ? (
                                  <>
                                    {/* Prospect lié — OBLIGATOIRE */}
                                    <div className="space-y-2">
                                        <Label>Prospect lié *</Label>
                                        <Select value={form.prospect_id || ''} onValueChange={(v) => setForm(p => ({ ...p, prospect_id: v }))}>
                                            <SelectTrigger data-testid="task-prospect-select"><SelectValue placeholder={prospectsLoaded ? 'Choisir un dossier' : 'Chargement…'} /></SelectTrigger>
                                            <SelectContent>
                                                {prospects.map(p => (
                                                    <SelectItem key={p.id} value={p.id}>{p.company_name || `${p.first_name} ${p.last_name}`}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-slate-400">Une tâche « dossier » est rattachée à un prospect.</p>
                                    </div>
                                    {/* Module — OBLIGATOIRE */}
                                    <div className="space-y-2">
                                        <Label>Mur du dossier concerné *</Label>
                                        <Select value={form.module} onValueChange={(v) => setForm(p => ({ ...p, module: v }))}>
                                            <SelectTrigger data-testid="task-module-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="easy_power">Easy Power</SelectItem>
                                                <SelectItem value="chantier">Chantier</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-slate-400">La tâche et ses changements de statut apparaîtront sur ce mur.</p>
                                    </div>
                                  </>
                                ) : (
                                    /* Thème — pour une tâche interne */
                                    <div className="space-y-2">
                                        <Label>Thème *</Label>
                                        <Select value={form.theme || ''} onValueChange={(v) => setForm(p => ({ ...p, theme: v }))}>
                                            <SelectTrigger data-testid="task-theme-select"><SelectValue placeholder="Choisir un thème" /></SelectTrigger>
                                            <SelectContent>
                                                {INTERNAL_THEMES.map(t => (
                                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-slate-400">Tâche organisationnelle, non liée à un dossier.</p>
                                    </div>
                                )}
                                <div className="space-y-2">
                                    <Label>Description</Label>
                                    <Textarea value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Détails supplémentaires..." rows={2} />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Priorité</Label>
                                        <Select value={form.priority} onValueChange={(v) => setForm(p => ({ ...p, priority: v }))}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="low">Basse</SelectItem>
                                                <SelectItem value="medium">Moyenne</SelectItem>
                                                <SelectItem value="high">Haute</SelectItem>
                                                <SelectItem value="urgent">Urgente</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Date limite</Label>
                                        <Input type="date" value={form.due_date} onChange={(e) => setForm(p => ({ ...p, due_date: e.target.value }))} />
                                    </div>
                                </div>
                                {/* Assigner à - multi */}
                                <div className="space-y-2">
                                    <Label>Assigner à <span className="text-xs text-slate-400">(plusieurs possibles)</span></Label>
                                    <div className="border border-slate-200 rounded-md max-h-44 overflow-y-auto bg-white">
                                        <label className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100">
                                            <input type="checkbox" className="w-4 h-4 rounded text-blue-600" checked={form.assignee_ids.includes(user?.id)}
                                                onChange={(e) => setForm(p => ({ ...p, assignee_ids: e.target.checked ? Array.from(new Set([...(p.assignee_ids || []), user.id])) : (p.assignee_ids || []).filter(id => id !== user?.id) }))}
                                                data-testid="task-assignee-me" />
                                            <span className="text-sm">Moi-même</span>
                                        </label>
                                        {assignableUsers.filter(u => u.id !== user?.id).map(u => (
                                            <label key={u.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0">
                                                <input type="checkbox" className="w-4 h-4 rounded text-blue-600" checked={form.assignee_ids.includes(u.id)}
                                                    onChange={(e) => setForm(p => ({ ...p, assignee_ids: e.target.checked ? Array.from(new Set([...(p.assignee_ids || []), u.id])) : (p.assignee_ids || []).filter(id => id !== u.id) }))}
                                                    data-testid={`task-assignee-${u.id}`} />
                                                <span className="text-sm">{u.first_name} {u.last_name}</span>
                                                <span className="text-xs text-slate-400">({u.role === 'admin_full' || u.role === 'admin' ? 'Admin' : u.role === 'vp' ? 'VP' : 'Apporteur'})</span>
                                            </label>
                                        ))}
                                    </div>
                                    {form.assignee_ids.length === 0 && <p className="text-xs text-slate-500">Si personne n'est sélectionné, la tâche vous sera assignée.</p>}
                                </div>
                                {isVP && (
                                    <div className="flex items-center gap-3 p-3 bg-violet-50 rounded-lg">
                                        <input type="checkbox" id="is_team_task" checked={form.is_team_task} onChange={(e) => setForm(p => ({ ...p, is_team_task: e.target.checked }))} className="w-4 h-4 rounded text-violet-600" />
                                        <label htmlFor="is_team_task" className="text-sm"><span className="font-medium text-violet-700">Tâche d'équipe</span><span className="text-violet-500 ml-1">(visible par tous les membres)</span></label>
                                    </div>
                                )}
                                <Button type="submit" className="w-full" disabled={saving} data-testid="save-task-btn">
                                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    {editingTask ? 'Mettre à jour' : 'Créer la tâche'}
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Cartes de statut CLIQUABLES */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    {STATUS_CARDS.map(c => (
                        <button key={c.key} type="button" onClick={() => setFilterStatus(c.key)}
                            className={cn('text-left rounded-xl border transition-all', c.bg, filterStatus === c.key ? `ring-2 ${c.active} border-transparent` : 'border-slate-200 hover:border-slate-300')}
                            data-testid={`status-card-${c.key}`}>
                            <div className="p-4 flex items-center justify-between">
                                <div>
                                    <p className={cn('text-xs uppercase', c.text)}>{c.label}</p>
                                    <p className={cn('text-2xl font-bold', c.text)}>{c.value}</p>
                                </div>
                                <c.Icon className={cn('w-7 h-7 opacity-70', c.text)} />
                            </div>
                        </button>
                    ))}
                </div>

                {/* Filtres */}
                <Card>
                    <CardContent className="pt-4">
                        <div className="flex flex-col lg:flex-row gap-3">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input placeholder="Rechercher (titre, dossier...)" className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="task-search" />
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                {/* Période */}
                                <Select value={filterPeriod} onValueChange={setFilterPeriod}>
                                    <SelectTrigger className="w-36" data-testid="filter-period"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Toute période</SelectItem>
                                        <SelectItem value="today">Aujourd'hui</SelectItem>
                                        <SelectItem value="week">Cette semaine</SelectItem>
                                        <SelectItem value="month">Ce mois</SelectItem>
                                    </SelectContent>
                                </Select>
                                {/* Équipe (admin) */}
                                {isAdmin && teams.length > 0 && (
                                    <Select value={filterTeam || 'all'} onValueChange={(v) => setFilterTeam(v === 'all' ? '' : v)}>
                                        <SelectTrigger className="w-40" data-testid="filter-team"><SelectValue placeholder="Équipe" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Toutes équipes</SelectItem>
                                            {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                )}
                                {/* Utilisateur (admin) / Membre (VP) */}
                                {(isAdmin || isVP) && (
                                    <Select value={filterUser || 'all'} onValueChange={(v) => setFilterUser(v === 'all' ? '' : v)}>
                                        <SelectTrigger className="w-44" data-testid="filter-user"><SelectValue placeholder={isVP ? 'Mon équipe' : 'Utilisateur'} /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">{isVP ? 'Toute mon équipe' : 'Tous les utilisateurs'}</SelectItem>
                                            <SelectItem value={user.id}>Moi ({user.first_name})</SelectItem>
                                            {(isAdmin ? assignableUsers : [...teamMembers, ...assignableUsers]).filter((u, i, arr) => u.id !== user.id && arr.findIndex(x => x.id === u.id) === i).map(u => (
                                                <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                                {/* Priorité */}
                                <Select value={filterPriority} onValueChange={setFilterPriority}>
                                    <SelectTrigger className="w-36" data-testid="filter-priority"><SelectValue placeholder="Priorité" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Toutes priorités</SelectItem>
                                        <SelectItem value="urgent">Urgente</SelectItem>
                                        <SelectItem value="high">Haute</SelectItem>
                                        <SelectItem value="medium">Moyenne</SelectItem>
                                        <SelectItem value="low">Basse</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Barre de suppression multiple (Full Admin) */}
                {isAdminFull && selectedIds.size > 0 && (
                    <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-red-50 border border-red-200 rounded-lg" data-testid="bulk-delete-bar">
                        <span className="text-sm font-medium text-red-700">{selectedIds.size} tâche(s) sélectionnée(s)</span>
                        <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>Annuler</Button>
                            <Button variant="destructive" size="sm" onClick={handleBulkDelete} data-testid="bulk-delete-btn">
                                <Trash2 className="w-4 h-4 mr-1" />Supprimer la sélection
                            </Button>
                        </div>
                    </div>
                )}

                {/* Tableau de pilotage */}
                <Card>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm min-w-[760px]" data-testid="tasks-table">
                                <thead>
                                    <tr className="text-left text-xs uppercase text-slate-500 border-b">
                                        {isAdminFull && (
                                            <th className="px-3 py-3 w-10">
                                                <input type="checkbox" className="w-4 h-4 rounded text-red-600 cursor-pointer"
                                                    checked={allDisplayedSelected} onChange={toggleSelectAll}
                                                    title="Tout sélectionner" data-testid="select-all-tasks" />
                                            </th>
                                        )}
                                        <th className="px-4 py-3 font-medium">Tâche</th>
                                        <th className="px-4 py-3 font-medium">Dossier</th>
                                        <th className="px-4 py-3 font-medium">Assigné(s)</th>
                                        <th className="px-4 py-3 font-medium">
                                            <button type="button" onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                                                className="flex items-center gap-1 uppercase hover:text-slate-800 transition-colors" data-testid="sort-due-date">
                                                Échéance
                                                {sortDir === 'asc'
                                                    ? <ArrowUp className="w-3.5 h-3.5" />
                                                    : <ArrowDown className="w-3.5 h-3.5" />}
                                                <span className="normal-case text-[10px] text-slate-400">{sortDir === 'asc' ? '(la + proche)' : '(la + loin)'}</span>
                                            </button>
                                        </th>
                                        <th className="px-4 py-3 font-medium">Statut</th>
                                        <th className="px-4 py-3 font-medium text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedTasks.length === 0 ? (
                                        <tr><td colSpan={isAdminFull ? 7 : 6} className="px-4 py-12 text-center text-slate-400">Aucune tâche pour ces filtres</td></tr>
                                    ) : displayedTasks.map(task => (
                                        <TaskRow key={task.id} task={task} canRemind={isAdmin || isVP || task.created_by === user.id}
                                            selectable={isAdminFull} selected={selectedIds.has(task.id)} onToggleSelect={toggleSelect}
                                            onStatusChange={handleStatusChange} onRemind={handleRemind} onEdit={openEdit} onDelete={handleDelete} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Dialog Prolongation : raison + nouvelle date plus lointaine (obligatoires) */}
            <Dialog open={!!postponeTask} onOpenChange={(o) => { if (!o) setPostponeTask(null); }}>
                <DialogContent className="max-w-md" data-testid="postpone-dialog">
                    <DialogHeader>
                        <DialogTitle>Prolonger la tâche 📅</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-slate-500">
                            La tâche sortira des « en retard ». Indiquez une raison et une nouvelle date <strong>plus lointaine</strong>.
                        </p>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Raison du prolongement <span className="text-red-500">*</span></Label>
                            <Textarea value={postponeReason} onChange={(e) => setPostponeReason(e.target.value)}
                                placeholder="Ex : client absent, en attente d'un document…" rows={3}
                                data-testid="postpone-reason" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Nouvelle date d'échéance <span className="text-red-500">*</span></Label>
                            <Input type="date" value={postponeDate} onChange={(e) => setPostponeDate(e.target.value)}
                                min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                                data-testid="postpone-date" />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setPostponeTask(null)} data-testid="postpone-cancel">Annuler</Button>
                            <Button onClick={submitPostpone} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="postpone-confirm">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Prolonger'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}

function TaskRow({ task, canRemind, selectable, selected, onToggleSelect, onStatusChange, onRemind, onEdit, onDelete }) {
    const overdue = isOverdue(task.due_date, task.status);
    const prio = getPriorityConfig(task.priority);
    const names = (task.assignee_names && task.assignee_names.length > 0) ? task.assignee_names : (task.assigned_to_name ? [task.assigned_to_name] : []);
    return (
        <tr className={cn('border-b last:border-0 hover:bg-slate-50/60', overdue && 'bg-red-50/40', selected && 'bg-red-50/70')} data-testid={`task-row-${task.id}`}>
            {selectable && (
                <td className="px-3 py-3 align-top">
                    <input type="checkbox" className="w-4 h-4 rounded text-red-600 cursor-pointer mt-1"
                        checked={!!selected} onChange={() => onToggleSelect(task.id)} data-testid={`select-task-${task.id}`} />
                </td>
            )}
            <td className="px-4 py-3 align-top">
                <div className="font-medium text-slate-900">{task.title}</div>
                <div className="mt-1 flex items-center gap-2">
                    <Badge className={cn('text-[10px]', prio.color)}><prio.Icon className="w-3 h-3 mr-1" />{prio.label}</Badge>
                    {task.module && <span className="text-[10px] text-slate-400">{task.module === 'chantier' ? 'Chantier' : 'Easy Power'}</span>}
                </div>
            </td>
            <td className="px-4 py-3 align-top">
                {task.category === 'interne'
                    ? <span className="flex items-center gap-1 text-violet-600"><Tag className="w-3 h-3" />{task.theme || 'Interne'}</span>
                    : (task.prospect_name ? <span className="flex items-center gap-1 text-blue-600"><Tag className="w-3 h-3" />{task.prospect_name}</span> : <span className="text-slate-300">—</span>)}
            </td>
            <td className="px-4 py-3 align-top">
                {names.length > 0 ? <span className="flex items-center gap-1 text-slate-700" title={names.join(', ')}><User className="w-3 h-3" />{names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`}</span> : <span className="text-slate-300">—</span>}
            </td>
            <td className="px-4 py-3 align-top">
                {task.due_date ? <span className={cn('flex items-center gap-1', overdue && 'text-red-600 font-medium')}><Calendar className="w-3 h-3" />{formatDate(task.due_date)}{overdue && ' ⚠'}</span> : <span className="text-slate-300">—</span>}
            </td>
            <td className="px-4 py-3 align-top">
                <Select value={task.status === 'in_progress' ? 'todo' : task.status} onValueChange={(v) => onStatusChange(task.id, v)}>
                    <SelectTrigger className="h-8 w-32" data-testid={`task-status-${task.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="todo">À faire</SelectItem>
                        <SelectItem value="postponed">Prolongée 📅</SelectItem>
                        <SelectItem value="done">Terminée</SelectItem>
                    </SelectContent>
                </Select>
                {task.status === 'postponed' && task.postpone_reason && (
                    <p className="mt-1 text-[11px] text-amber-700 max-w-[160px] truncate" title={task.postpone_reason}>↳ {task.postpone_reason}</p>
                )}
            </td>
            <td className="px-4 py-3 align-top">
                <div className="flex items-center justify-end gap-1">
                    {canRemind && task.status !== 'done' && (
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-amber-600 hover:text-amber-700" title="Relancer (email + notif)" onClick={() => onRemind(task.id)} data-testid={`task-remind-${task.id}`}>
                            <Bell className="w-4 h-4" />
                        </Button>
                    )}
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Modifier" onClick={() => onEdit(task)} data-testid={`task-edit-${task.id}`}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-600" title="Supprimer" onClick={() => onDelete(task.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
            </td>
        </tr>
    );
}
